import React, { useState, useRef } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { UniqueIdentifier } from '@dnd-kit/core'

// evertel
import { Icon, Button } from '@evertel/web/ui'
import { DocumentServiceProvider } from '@evertel/service'
import { WidgetsProvider, Delegator } from '@evertel/schema-parser'

import { WriteWidgets } from '../schema-widgets'
import { DnDElement } from './DnDElement'
import { DragData, ParsedRow, ParsedItem, isSpecialRow } from './types'
import { MAX_ELEMENTS_PER_ROW, PLACEHOLDER_ELEMENT_ID } from './constants'

interface DnDRowProps {
    row: ParsedRow
    activeId: UniqueIdentifier | null
    activeDragData: DragData | null
    isDropTarget?: boolean
    schemaId: number
    onDelete: () => void
    onDeleteElement: (fieldId: string) => void
    onEditElement: (item: ParsedItem) => void
    onClickToEditListSchema?: (internalSchemaId: string) => void
}

/**
 * Sortable row component that handles all row types.
 * Row type is always 'row' - the item type determines rendering:
 * - Single spacer item: renders SpacerRowContent
 * - Single list item: renders ListRowContent
 * - Other items: renders as regular row with DnDElements
 */
export const DnDRow: React.FC<DnDRowProps> = ({
    row,
    activeId,
    activeDragData,
    isDropTarget,
    schemaId,
    onDelete,
    onDeleteElement,
    onEditElement,
    onClickToEditListSchema
}) => {
    // Track focus within the row for accessibility
    const [isFocusWithin, setIsFocusWithin] = useState(false)
    const rowRef = useRef<HTMLDivElement>(null)

    // Check if we're dragging an element
    const isDraggingElement = activeDragData?.type === 'element'

    // Check if this is a special row (single spacer or list item)
    const singleItem = row.items.length === 1 ? row.items[0] : null
    const isSpacerRow = singleItem?.type === 'spacer'
    const isListRow = singleItem?.type === 'list'

    // Determine layout type for drag data and CSS class
    const layoutType = isSpacerRow ? 'spacer' : isListRow ? 'list' : 'row'
    const layoutTypeDisplay = isListRow ? 'sub-form' : layoutType


    // Make the row sortable (can be reordered among other rows)
    const {
        attributes,
        listeners,
        setNodeRef: setSortableRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
        over
    } = useSortable({
        id: `row-${row.index}`,
        data: {
            type: 'layout',
            layoutType,
            fromToolbox: false,
            rowIndex: row.index
        } as DragData
    })

    // Create a droppable target for empty rows
    // This gives us a dedicated drop target that won't conflict with row sorting
    const { setNodeRef: setEmptyDroppableRef } = useDroppable({
        id: `row-${row.index}-empty`,
        data: {
            type: 'element',
            rowIndex: row.index
        } as DragData,
        disabled: row.items.length > 0 // Only active when row is empty
    })

    // Count real elements (excluding placeholder)
    const realElementCount = row.items.filter(item => item.id !== PLACEHOLDER_ELEMENT_ID).length
    const hasElements = row.items.length > 0
    const isFull = realElementCount >= MAX_ELEMENTS_PER_ROW

    // For greying out full rows when dragging elements
    const isActiveDragInThisRow = activeId && row.items.some(item => item.id === activeId)
    const countForGreyOut = isActiveDragInThisRow
        ? row.items.filter(item => item.id !== PLACEHOLDER_ELEMENT_ID && item.id !== activeId).length
        : realElementCount
    const shouldGreyOut = (countForGreyOut >= MAX_ELEMENTS_PER_ROW || isListRow )&& isDraggingElement

    // Check if this row is being hovered over
    const isOverThisRow = over?.id === `row-${row.index}` || row.items.some(item => item.id === over?.id)
    const canAcceptDrop = isDraggingElement && !isFull && !isSpecialRow(row)

    // Only apply transform/transition during active drag
    // This prevents the jump-back-then-animate issue when drag ends
    const style = {
        transform: activeId ? CSS.Transform.toString(transform) : undefined,
        transition: activeId ? transition : undefined,
        opacity: isDragging ? 0.5 : 1
    }

    // Calculate element width based on count
    const elementWidth = row.items.length > 0
        ? `${100 / Math.min(row.items.length, MAX_ELEMENTS_PER_ROW)}%`
        : '100%'

    // Handle focus events for accessibility
    const handleFocusIn = () => setIsFocusWithin(true)
    const handleFocusOut = (e: React.FocusEvent) => {
        if (rowRef.current && !rowRef.current.contains(e.relatedTarget as Node)) {
            setIsFocusWithin(false)
        }
    }

    // Combine refs for the row container
    const setRefs = (node: HTMLDivElement | null) => {
        setSortableRef(node)
        rowRef.current = node
    }

    return (
        <div
            ref={setRefs}
            style={style}
            className={`sb-row sb-row--${layoutType} ${isDragging ? 'shadow-lg' : ''}`}
            onFocus={handleFocusIn}
            onBlur={handleFocusOut}
        >
            {/* Drop indicator line for toolbox layout items */}
            {isDropTarget && (
                <div className="sb-row__drop-indicator" />
            )}

            <div className={`sb-row__container d-flex ${isOverThisRow && canAcceptDrop && !hasElements ? 'border-primary' : ''} ${shouldGreyOut ? 'opacity-50' : ''}`}>
                {/* Row Controls - shared by all row types */}
                <div className="sb-row__controls">
                    <button
                        ref={setActivatorNodeRef}
                        {...attributes}
                        {...listeners}
                        className="btn btn-sm btn-link p-0 mb-1"
                        title="Drag to reorder"
                        aria-label={`Drag to reorder ${layoutTypeDisplay}`}
                    >
                        <Icon name="arrows-up-down-left-right" fixedWidth />
                    </button>
                    <button
                        className="btn btn-sm btn-link text-danger p-0"
                        onClick={onDelete}
                        title={`Delete ${layoutTypeDisplay}`}
                        aria-label={`Delete ${layoutTypeDisplay}`}
                    >
                        <Icon name="trash-can" fixedWidth />
                    </button>
                </div>

                {/* Row Content - varies by type */}
                {isSpacerRow && singleItem ? (
                    <SpacerContent
                        item={singleItem}
                        onEdit={() => onEditElement(singleItem)}
                    />
                ) : isListRow && singleItem ? (
                    <ListContent
                        item={singleItem}
                        rowIndex={row.index}
                        schemaId={schemaId}
                        showActions={isFocusWithin}
                        onEditProperties={() => onEditElement(singleItem)}
                        onManageSubForm={onClickToEditListSchema}
                        isDraggingElement={isDraggingElement}
                    />
                ) : (
                    <PlainRowContent
                        row={row}
                        activeId={activeId}
                        schemaId={schemaId}
                        elementWidth={elementWidth}
                        isDraggingElement={isDraggingElement}
                        isFull={isFull}
                        isOverThisRow={isOverThisRow}
                        canAcceptDrop={canAcceptDrop}
                        setEmptyDroppableRef={setEmptyDroppableRef}
                        onDeleteElement={onDeleteElement}
                        onEditElement={onEditElement}
                    />
                )}
            </div>
        </div>
    )
}

/**
 * Spacer content
 */
interface SpacerContentProps {
    item: ParsedItem
    onEdit: () => void
}

const SpacerContent: React.FC<SpacerContentProps> = ({ item, onEdit }) => {
    const height = (item.height as number) || 30
    const border = (item.border as boolean) || false

    return (
        <div
            className="sb-row__content"
            style={{ height: `${Math.max(height, 40)}px` }}
        >
            <div className="text-muted">
                <Icon name="arrows-up-down" className="me-2" />
                Spacer ({height}px)
                <Button
                    color="primary"
                    outline
                    size="sm"
                    className="ms-3"
                    onClick={onEdit}
                    title="Edit spacer"
                    aria-label="Edit spacer"
                >
                    <Icon name="pencil" />
                </Button>
            </div>
            {border && (
                <hr className="text-primary w-100 mb-n1 mt-1" />
            )}
        </div>
    )
}

/**
 * List/SubForm content
 */
interface ListContentProps {
    item: ParsedItem
    rowIndex: number
    schemaId: number
    showActions: boolean
    onEditProperties: () => void
    onManageSubForm?: (internalSchemaId: string) => void
    isDraggingElement: boolean
}

const ListContent: React.FC<ListContentProps> = ({
    item,
    rowIndex,
    schemaId,
    showActions,
    onEditProperties,
    onManageSubForm,
    isDraggingElement
}) => {
    const [isHovered, setIsHovered] = useState(false)

    const listId = (item.internalSchemaId as string) || item.id
    const listTitle = (item.title as string) || 'Sub-Form'

    // Show overlay when hovered OR when row has focus
    const showOverlay = (isHovered || showActions) && !isDraggingElement

    return (
        <div
            className="sb-row__content"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Sub-form preview */}
            <div inert>
                <DocumentServiceProvider services={[{
                    id: 'meta', value: { scope: 'schemaBuilder' }
                }]}>
                    <WidgetsProvider value={WriteWidgets}>
                        <Delegator
                            id={item.id}
                            type={item.type}
                            scope="schemaBuilder"
                            schemaId={schemaId}
                            {...item}
                            index={(item.index ?? rowIndex).toString()}
                        />
                    </WidgetsProvider>
                </DocumentServiceProvider>
            </div>

            {/* Label badge */}
            <div className="position-absolute bottom-0 end-0 p-2">
                <span className="badge bg-secondary">
                    <Icon name="table-list" className="me-1" />
                    {listTitle}
                </span>
            </div>

            {/* Action overlay - visible on hover or focus */}
            {showOverlay && (
                <div className="sb-subform__overlay">
                    {listId && onManageSubForm && (
                        <Button
                            color="secondary"
                            onClick={() => onManageSubForm(listId)}
                        >
                            Manage Sub Form
                        </Button>
                    )}
                    <Button
                        color="secondary"
                        className="ms-3"
                        onClick={onEditProperties}
                        title="Edit sub-form properties"
                        aria-label="Edit sub-form properties"
                    >
                        <Icon name="pencil" />
                    </Button>
                </div>
            )}
        </div>
    )
}

/**
 * Plain row content with elements
 */
interface PlainRowContentProps {
    row: ParsedRow
    activeId: UniqueIdentifier | null
    schemaId: number
    elementWidth: string
    isDraggingElement: boolean
    isFull: boolean
    isOverThisRow: boolean
    canAcceptDrop: boolean
    setEmptyDroppableRef: (node: HTMLElement | null) => void
    onDeleteElement: (fieldId: string) => void
    onEditElement: (item: ParsedItem) => void
}

const PlainRowContent: React.FC<PlainRowContentProps> = ({
    row,
    activeId,
    schemaId,
    elementWidth,
    isDraggingElement,
    isFull,
    isOverThisRow,
    canAcceptDrop,
    setEmptyDroppableRef,
    onDeleteElement,
    onEditElement
}) => {
    return (
        <div className="sb-row__content">
            <SortableContext
                items={row.items.map(item => item.id)}
                strategy={horizontalListSortingStrategy}
            >
                {row.items.length === 0 ? (
                    <div
                        ref={setEmptyDroppableRef}
                        className={`sb-row__empty-placeholder ${isOverThisRow && canAcceptDrop ? 'border-primary' : ''}`}
                    >
                        <span className="text-content">{isDraggingElement && !isFull ? 'Drop element here' : 'Empty row - drag elements here'}</span>
                    </div>
                ) : (
                    <div className="sb-row__elements d-flex w-100 gap-2">
                        {row.items.map((element) => (
                            <div
                                key={element.id}
                                style={{ width: elementWidth, minWidth: 0 }}
                            >
                                <DnDElement
                                    element={element}
                                    rowIndex={row.index}
                                    schemaId={schemaId}
                                    activeId={activeId}
                                    isDraggingElement={isDraggingElement}
                                    onDelete={() => onDeleteElement(element.id)}
                                    onEdit={() => onEditElement(element)}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </SortableContext>
        </div>
    )
}
